import { Suspense } from 'react';

import { Tag } from '@spotify-internal/encore-web';
import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  ActivityChangeOptions,
  ActivityItem,
} from '@spotify/plugin-confidence-activity-react';
import {
  ISSUE_ACTIVITY_TYPES,
  createIssueUpdatesChangeExtension,
} from '@spotify/plugin-confidence-issues-react';
import { useSurfaceRefSuspense } from '@spotify/plugin-confidence-surfaces-react';

import { isIssueActivityType } from './issueActivityUtils';

import styles from './IssueSurfacesChangeExtension.module.css';

type SurfaceItem = {
  name: string;
  displayName?: string;
};

const getSurfaces = (value: unknown): SurfaceItem[] => {
  if (!value) {
    return [];
  }

  if (typeof value === 'string') {
    return [{ name: value }];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  const surfaces: SurfaceItem[] = [];

  for (const item of value) {
    if (typeof item === 'string') {
      surfaces.push({ name: item });
    } else if (
      typeof item === 'object' &&
      item !== null &&
      'name' in item &&
      typeof item.name === 'string'
    ) {
      const displayName =
        'displayName' in item && typeof item.displayName === 'string'
          ? item.displayName
          : undefined;

      surfaces.push({ name: item.name, displayName });
    }
  }

  return surfaces;
};

const analyzeSurfaceChanges = (change: {
  oldValue?: unknown;
  newValue?: unknown;
}) => {
  const oldSurfaces = getSurfaces(change.oldValue);
  const newSurfaces = getSurfaces(change.newValue);

  const added = newSurfaces.filter(
    surface =>
      !oldSurfaces.some(oldSurface => oldSurface.name === surface.name),
  );
  const removed = oldSurfaces.filter(
    oldSurface =>
      !newSurfaces.some(surface => surface.name === oldSurface.name),
  );

  return { added, removed };
};

const getSurfaceDisplayName = (surface: SurfaceItem): string =>
  surface.displayName ?? extractLastNameComponent(surface.name) ?? surface.name;

type SurfaceActivityTagProps = {
  surface: SurfaceItem;
  added: boolean;
};

const SurfaceActivityTagFallback = ({
  surface,
  added,
}: SurfaceActivityTagProps) => (
  <Tag
    {...(added
      ? { colorSet: 'positiveSubdued' as const }
      : { className: styles.removedTag })}
  >
    {getSurfaceDisplayName(surface)}
  </Tag>
);

const SurfaceActivityTag = ({ surface, added }: SurfaceActivityTagProps) => {
  const { surface: surfaceRef } = useSurfaceRefSuspense({
    name: surface.name,
  });

  return (
    <SurfaceActivityTagFallback
      surface={{ ...surface, displayName: surfaceRef.displayName }}
      added={added}
    />
  );
};

const IssueSurfacesChangeRender = ({ change }: ActivityChangeOptions) => {
  const { added, removed } = analyzeSurfaceChanges(change);

  return (
    <ActivityItem.Section label="Surfaces">
      <div className={styles.tags}>
        {added.map(surface => (
          <Suspense
            key={surface.name}
            fallback={<SurfaceActivityTagFallback surface={surface} added />}
          >
            <SurfaceActivityTag surface={surface} added />
          </Suspense>
        ))}
        {removed.map(surface => (
          <Suspense
            key={surface.name}
            fallback={
              <SurfaceActivityTagFallback surface={surface} added={false} />
            }
          >
            <SurfaceActivityTag surface={surface} added={false} />
          </Suspense>
        ))}
      </div>
    </ActivityItem.Section>
  );
};

export const ISSUE_SURFACES_CHANGE_EXTENSION =
  createIssueUpdatesChangeExtension({
    id: 'issue-surfaces-change',
    priority: 10,
    predicate: options => {
      if (
        !isIssueActivityType(options.activity, ISSUE_ACTIVITY_TYPES.updated) ||
        options.change.fieldPath !== '/surfaces'
      ) {
        return false;
      }

      const { added, removed } = analyzeSurfaceChanges(options.change);

      return added.length > 0 || removed.length > 0;
    },
    render: IssueSurfacesChangeRender,
  });
